import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Scissors, ArrowRight } from "lucide-react";
import { LainaCta } from "@/components/common/LainaCta";
import AffiliateAds from "@/components/common/AffiliateAds";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";

const PienkonelainaPage = () => {
  const seoData = getEnhancedSeoDataForRoute("/lainat/pienkonelaina");
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/lainat/pienkonelaina"
        schema={seoData.schema}
      />
      
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-8 flex items-center">
          <Scissors className="h-10 w-10 mr-4 text-blue-600" />
          <h1 className="text-3xl font-bold text-blue-700">Pienkonelaina</h1>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-16">
          <div className="lg:col-span-2 space-y-8 md:space-y-12">
            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <h2 className="text-xl font-semibold mb-4 text-blue-700">Rahoitus pienkoneisiin ja työkaluihin</h2>
              <p className="text-gray-700 mb-4">
                Pienkonelaina on erityisesti puutarhakoneiden, työkalujen ja muiden pienkoneiden hankintaan tarkoitettu rahoitusmuoto. 
                Sen avulla voit hankkia tarvitsemasi ruohonleikkurin, moottorisahan, lumilinkoon tai muihin pienkoneisiin.
              </p>
              <p className="text-gray-700">
                Lainasampo.fi:n kautta voit vertailla eri rahoituslaitosten tarjoamia vaihtoehtoja ja löytää itsellesi edullisimman 
                tavan rahoittaa pienkonehankintasi.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle className="text-blue-700">Pienkonelainan edut</CardTitle>
                  <CardDescription>Miksi rahoittaa pienkoneet lainalla?</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Mahdollisuus hankkia laadukkaampia koneita</li>
                    <li>Ei tarvitse käyttää säästöjä kertahankintaan</li>
                    <li>Joustavat maksuehdot ja -ajat</li>
                    <li>Nopea rahoituspäätös</li>
                    <li>Yleensä ei vaadi erillistä vakuutta</li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-blue-700">Näin haet pienkonelainaa</CardTitle>
                  <CardDescription>Pienkoneiden rahoittaminen on helppoa</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                    <li>Arvioi tarvitsemasi lainasumma koneen hinnan perusteella</li>
                    <li>Täytä lainahakemus Lainasampo.fi:n kautta</li>
                    <li>Vertaile eri rahoitusyhtiöiden tarjouksia</li>
                    <li>Valitse itsellesi sopivin rahoitusvaihtoehto</li>
                    <li>Hanki tarvitsemasi koneet ja aloita työt tai harrastus</li>
                  </ol>
                </CardContent>
              </Card>
            </div>
            
            <div className="mb-10">
              <h3 className="text-xl font-semibold mb-4 text-blue-700">Mitä pienkonelainalla voi rahoittaa?</h3>
              <p className="text-gray-700 mb-4">
                Pienkonelaina soveltuu monenlaisiin hankintoihin:
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-md">
                  <h4 className="font-semibold text-blue-700 mb-2">Puutarhakoneet</h4>
                  <p className="text-sm text-gray-600">Ruohonleikkurit, trimmerit, oksasilppurit, lehtipuhaltimet</p>
                </div>
                <div className="bg-green-50 p-4 rounded-md">
                  <h4 className="font-semibold text-green-700 mb-2">Metsäkoneet</h4>
                  <p className="text-sm text-gray-600">Moottorisahat, raivaussahat, halkaisukoneet</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-md">
                  <h4 className="font-semibold text-purple-700 mb-2">Talvikoneet</h4>
                  <p className="text-sm text-gray-600">Lumilingot, lumiaurat, hiekoittimet</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-md">
                  <h4 className="font-semibold text-orange-700 mb-2">Maastoajoneuvot</h4>
                  <p className="text-sm text-gray-600">Mönkijät, moottorikelkat, pientraktorit</p>
                </div>
                <div className="bg-red-50 p-4 rounded-md">
                  <h4 className="font-semibold text-red-700 mb-2">Rakennuskoneet</h4>
                  <p className="text-sm text-gray-600">Betonimyllyt, sirkkelit, naulaimet</p>
                </div>
                <div className="bg-teal-50 p-4 rounded-md">
                  <h4 className="font-semibold text-teal-700 mb-2">Sähkötuotanto</h4>
                  <p className="text-sm text-gray-600">Aggregaatit, aurinkopaneelit, tuulivoimalat</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4 text-blue-700">Lue myös nämä artikkelit</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/artikkelit/lainan-ottamisen-opas-2025" className="text-blue-600 hover:underline flex items-center">
                    <ArrowRight size={16} className="mr-2" />
                    Lainan ottamisen opas 2025 - Mitä tulee tietää
                  </Link>
                </li>
                <li>
                  <Link to="/artikkelit/miten-valita-oikea-laina-eri-tilanteisiin-2025" className="text-blue-600 hover:underline flex items-center">
                    <ArrowRight size={16} className="mr-2" />
                    Miten valita oikea laina eri tilanteisiin 2025
                  </Link>
                </li>
                <li>
                  <Link to="/artikkelit/miten-inflaation-hidastuminen-vaikutti-lainojen-korkoihin-2024" className="text-blue-600 hover:underline flex items-center">
                    <ArrowRight size={16} className="mr-2" />
                    Miten inflaation hidastuminen vaikutti lainojen korkoihin 2024
                  </Link>
                </li>
              </ul>
            </div>
            
            <AffiliateAds />
          </div>
          
          <div className="md:w-5/12">
            <div className="sticky top-24">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-lg mb-6">
                <h2 className="text-xl font-semibold mb-4">Kilpailuta pienkonelaina nyt!</h2>
                <p className="mb-4">
                  Täyttämällä yhden hakemuksen saat tarjoukset useista rahoituslaitoksista. 
                  Vertaile ja valitse edullisin rahoitus pienkonehankintaasi!
                </p>
                <LainaCta 
                  variant="primary" 
                  size="lg" 
                  text="Hae pienkonelainaa" 
                  className="bg-white text-blue-600 hover:bg-blue-50 w-full"
                />
              </div>
              
              <Card className="mb-6 border-blue-200 bg-blue-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-blue-700">Pienkonelaina lyhyesti</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Laina-aika:</span>
                      <span className="font-medium">1-5 vuotta</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lainasumma:</span>
                      <span className="font-medium">500€ - 15 000€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Korko alkaen:</span>
                      <span className="font-medium">6,9%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Vakuus:</span>
                      <span className="font-medium">Yleensä vakuudeton</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Käsittelyaika:</span>
                      <span className="font-medium">1-2 arkipäivää</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                <h3 className="font-semibold text-lg mb-3 text-blue-700">Katso myös nämä rahoitusvaihtoehdot</h3>
                <div className="space-y-2">
                  <Link to="/lainat/kulutusluotto" className="block p-2 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors">
                    Kulutusluotto
                  </Link>
                  <Link to="/lainat/joustoluotto" className="block p-2 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors">
                    Joustoluotto
                  </Link>
                  <Link to="/lainat/moottoripyoralaina" className="block p-2 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors">
                    Moottoripyörälaina
                  </Link>
                  <Link to="/lainat/pikalaina" className="block p-2 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors">
                    Pikalaina
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PienkonelainaPage;
